/**
 * News Manager Page
 * CRUD interface for managing news articles with draft/publish workflow
 */
import { useState } from 'react'
import {
  useNewsArticles,
  useCreateNewsArticle,
  useUpdateNewsArticle,
  useDeleteNewsArticle,
} from '../hooks/useNews'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Spinner } from '../components/ui/Spinner'
import { Alert } from '../components/ui/Alert'
import { NewsForm } from '../components/NewsForm'
import { Newspaper, Plus, Edit2, Trash2 } from 'lucide-react'
import type { NewsArticle, NewsArticleInput } from '../types'

export function NewsPage() {
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null)

  const { data, isLoading, error } = useNewsArticles()
  const createMutation = useCreateNewsArticle()
  const updateMutation = useUpdateNewsArticle()
  const deleteMutation = useDeleteNewsArticle()

  const handleCreate = async (formData: NewsArticleInput) => {
    await createMutation.mutateAsync(formData)
    setIsAddModalOpen(false)
  }

  const handleUpdate = async (formData: NewsArticleInput) => {
    if (!editingArticle) return
    await updateMutation.mutateAsync({ id: editingArticle.id, data: formData })
    setEditingArticle(null)
  }

  const handleDelete = async (article: NewsArticle) => {
    if (!confirm(`Delete "${article.title}"? This action cannot be undone.`)) return
    await deleteMutation.mutateAsync(article.id)
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getStatus = (article: NewsArticle) => {
    if (!article.published_at) return 'draft'
    if (article.published_at > Math.floor(Date.now() / 1000)) return 'scheduled'
    return 'published'
  }

  if (isLoading) return <Spinner />
  if (error) return <Alert variant="error">{(error as Error).message}</Alert>

  const articles = data?.articles || []

  // Filter articles
  const filteredArticles = articles.filter((article) => {
    const status = getStatus(article)
    if (filter === 'published') return status === 'published'
    if (filter === 'draft') return status === 'draft'
    return true
  })

  return (
    <>
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-coolgray shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading text-rhs-maroon truncate flex items-center gap-2">
                <Newspaper size={28} />
                News Manager
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {articles.length} total articles
              </p>
            </div>

            <Button variant="primary" onClick={() => setIsAddModalOpen(true)} className="shrink-0">
              <Plus size={16} />
              New Article
            </Button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {(['all', 'published', 'draft'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  filter === tab
                    ? 'bg-rhs-maroon text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Article List */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {filteredArticles.length === 0 ? (
          <Alert variant="info">No articles found.</Alert>
        ) : (
          <div className="space-y-4">
            {filteredArticles.map((article) => {
              const status = getStatus(article)
              return (
                <div
                  key={article.id}
                  className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {article.title}
                      </h3>
                      {article.excerpt && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{article.excerpt}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span
                          className={`px-2 py-1 rounded-full font-medium ${
                            status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : status === 'scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {status}
                        </span>
                        {article.published_at && <span>{formatDate(article.published_at)}</span>}
                        {article.author && <span>by {article.author}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingArticle(article)}
                      >
                        <Edit2 size={14} />
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(article)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add Article Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="New Article">
        <NewsForm
          onSubmit={handleCreate}
          onCancel={() => setIsAddModalOpen(false)}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      {/* Edit Article Modal */}
      <Modal isOpen={!!editingArticle} onClose={() => setEditingArticle(null)} title="Edit Article">
        {editingArticle && (
          <NewsForm
            article={editingArticle}
            onSubmit={handleUpdate}
            onCancel={() => setEditingArticle(null)}
            isSubmitting={updateMutation.isPending}
          />
        )}
      </Modal>
    </>
  )
}
